import { Controller } from '@hotwired/stimulus';

// Reveals one of the two branches a SplitTestComponent ships in the same response. The server can't pick on
// a CDN-cached page — Cloudflare would cache one visitor's variant and serve it to everybody — so posthog-js
// resolves the flag here instead.
//
// Calling getFeatureFlag IS the exposure, so this stops asking once it gets a variant, and only asks while
// the element is actually displayed. Two populations are deliberately never bucketed, and both keep the
// fallback branch: anyone
// posthog-js can't reach, and anyone whose branch is hidden by CSS — `.pd-only-desktop` and friends ship in
// the DOM on every device and are picked pre-paint by _platform_variants.scss, so a handheld visitor would
// otherwise be counted into a test they can't see.
//
// Class toggles only, never node removal: extension_install_tracker reads a disappearing install alert as
// proof the extension deleted it, so removing nodes inside a panel it watches would fabricate installs.
//
// Above-the-fold only. Exposure fires on connect, so a branch further down the page would count visitors who
// never scrolled to it; that case wants an IntersectionObserver around #resolve.

// Mirrors SplitTestComponent::VARIANTS. Anything else — flag disabled, key mistyped, or variants renamed in
// PostHog — leaves the fallback branch alone rather than guessing.
const CONTROL = 'control';
const VARIANTS = [CONTROL, 'test'];

export default class extends Controller {
  static targets = ['control', 'test'];
  static values = { key: String, resolved: String, override: String };

  connect() {
    // ?split_test=<variant> in dev/test (SplitTestOverride). Deliberately short-circuits posthog entirely,
    // so previewing a branch locally can't fire an exposure or reach the production project.
    if (VARIANTS.includes(this.overrideValue)) {
      this.#apply(this.overrideValue);
      return;
    }

    // Undefined wherever the snippet is skipped — outside production, and for excluded accounts.
    if (!window.posthog?.onFeatureFlags) return;

    this.unsubscribe = window.posthog.onFeatureFlags(() => this.#resolve());
  }

  disconnect() {
    // The pre-load stub queues callbacks instead of subscribing, so there may be nothing to hand back.
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  #resolve() {
    if (this.read || !this.#displayed()) return;

    // Flag off, key mistyped, or variants renamed in PostHog: the fallback branch stands. Silent by design —
    // a key that never resolves shows up as "No exposures yet" the moment anyone tries to launch it.
    const variant = window.posthog.getFeatureFlag(this.keyValue);

    // Only a real answer is final. onFeatureFlags fires first with posthog's localStorage-cached flags, which
    // for a returning visitor can predate the experiment; latching on that would strand them on the fallback
    // for the pageview and drop their exposure. posthog dedupes $feature_flag_called, so re-asking is free.
    if (!VARIANTS.includes(variant)) return;
    this.read = true;

    this.#apply(variant);
  }

  #apply(variant) {
    this.controlTarget.classList.toggle('d-none', variant !== CONTROL);
    this.testTarget.classList.toggle('d-none', variant === CONTROL);

    // Written before the event so listeners (and feature specs) can read the resolved variant off the DOM.
    this.resolvedValue = variant;
    this.dispatch('resolved', { detail: { key: this.keyValue, variant } });
  }

  // display:none only: a branch can spend an entrance animation at opacity 0 and must still resolve there,
  // well before anyone can see the swap.
  #displayed() {
    return this.element.checkVisibility?.() ?? this.element.offsetParent !== null;
  }
}
